import {
  ProductHistoryPageResponseSchema,
  ProductHistoryQuerySchema,
  productHistoryPath,
  SearchContentPageResponseSchema,
  SearchWorkspaceContentQuerySchema,
  searchWorkspaceContentPath,
  SecurityAuditPageResponseSchema,
  SecurityAuditQuerySchema,
  securityAuditPath,
  type SupporterCapability,
} from "@oyako-flow/contracts";
import { z } from "zod";

export interface WebMcpTool {
  name: string;
  title: string;
  description: string;
  inputSchema: Record<string, unknown>;
  annotations: {
    readOnlyHint: boolean;
    untrustedContentHint: boolean;
  };
  execute: (input: Record<string, unknown>, options: { signal: AbortSignal }) => Promise<unknown>;
}

interface WebMcpModelContext {
  registerTool?: (tool: WebMcpTool, options: { signal: AbortSignal }) => Promise<void>;
}

export interface WebMcpDocument {
  modelContext?: WebMcpModelContext;
}

export interface WebMcpRegistration {
  supported: boolean;
  dispose: () => void;
}

export type WebMcpCoreRead = (path: string, signal: AbortSignal) => Promise<unknown>;

export type ParentWebMcpView =
  | "today"
  | "dashboard"
  | "inbox"
  | "plan"
  | "routines"
  | "projects"
  | "organization"
  | "search"
  | "review"
  | "settings";

export interface ParentWebMcpContext {
  view: ParentWebMcpView;
  isGuardian: boolean;
  capabilities: ReadonlySet<SupporterCapability>;
  notificationCenterOpen: boolean;
}

const taskTodayReadTools = new Set(["oyako_list_today_tasks_v1", "oyako_list_carryover_tasks_v1"]);
const taskPlanReadTools = new Set([
  "oyako_list_planned_tasks_v1",
  "oyako_list_backlog_tasks_v1",
  "oyako_list_carryover_tasks_v1",
]);
const taskSharedReadTools = new Set(["oyako_get_task_structure_v1"]);
const taskSharedWriteTools = new Set([
  "oyako_add_task_checklist_item_v1",
  "oyako_update_task_checklist_item_v1",
  "oyako_set_task_checklist_item_state_v1",
  "oyako_add_task_dependency_v1",
  "oyako_remove_task_dependency_v1",
  "oyako_create_task_v1",
  "oyako_reschedule_task_v1",
  "oyako_reopen_task_v1",
  "oyako_change_task_plan_state_v1",
]);
const taskTodayWriteTools = new Set(["oyako_reorder_today_tasks_v1"]);
const routineReadTools = new Set([
  "oyako_list_routines_v1",
  "oyako_list_routine_versions_v1",
  "oyako_compare_routine_versions_v1",
]);
const routineWriteTools = new Set([
  "oyako_preview_routine_create_v1",
  "oyako_apply_routine_create_v1",
]);
const guardianRoutineTools = new Set([
  "oyako_list_calendars_v1",
  "oyako_create_calendar_v1",
  "oyako_update_calendar_v1",
  "oyako_set_calendar_archive_v1",
  "oyako_list_routine_templates_v1",
  "oyako_create_routine_template_v1",
  "oyako_update_routine_template_v1",
  "oyako_set_routine_template_archive_v1",
  "oyako_list_routine_reconciliations_v1",
  "oyako_reconcile_routines_v1",
]);
const reviewReadTools = new Set(["oyako_list_family_cycles_v1"]);
const guardianReviewWriteTools = new Set([
  "oyako_save_family_cycle_review_v1",
  "oyako_apply_family_cycle_action_v1",
  "oyako_dismiss_family_cycle_action_v1",
]);
const notificationPreferenceTools = new Set([
  "oyako_list_notification_preferences_v1",
  "oyako_save_notification_preference_v1",
]);
const notificationCenterTools = new Set([
  "oyako_list_notifications_v1",
  "oyako_set_notification_state_v1",
]);

/**
 * Keep page-defined tools no broader than the normal UI that is currently visible.
 * Core API authorization still rechecks every call; this prevents capability discovery
 * from exposing unrelated or guardian-only actions to a supporter in the first place.
 */
export function selectParentWebMcpTools(
  tools: readonly WebMcpTool[],
  context: ParentWebMcpContext,
): WebMcpTool[] {
  const can = (capability: SupporterCapability) =>
    context.isGuardian || context.capabilities.has(capability);

  return tools.filter((tool) => {
    const { name } = tool;
    if (context.notificationCenterOpen && notificationCenterTools.has(name)) {
      return context.isGuardian;
    }
    if (context.view === "dashboard" && name === "oyako_read_selected_child_dashboard_v1") {
      return can("insights:read");
    }
    if (context.view === "today") {
      if (taskTodayReadTools.has(name) || taskSharedReadTools.has(name)) return can("tasks:read");
      if (taskTodayWriteTools.has(name) || taskSharedWriteTools.has(name)) {
        return can("tasks:read") && can("tasks:write");
      }
      return false;
    }
    if (context.view === "plan") {
      if (taskPlanReadTools.has(name) || taskSharedReadTools.has(name)) return can("tasks:read");
      if (taskSharedWriteTools.has(name)) return can("tasks:read") && can("tasks:write");
      return false;
    }
    if (context.view === "routines") {
      if (routineReadTools.has(name)) return can("routines:read");
      if (routineWriteTools.has(name)) return can("routines:read") && can("routines:write");
      return context.isGuardian && guardianRoutineTools.has(name);
    }
    if (context.view === "review") {
      if (reviewReadTools.has(name)) return can("reviews:read");
      return context.isGuardian && guardianReviewWriteTools.has(name);
    }
    if (context.view === "inbox") {
      return context.isGuardian && name === "oyako_capture_inbox_item_v1";
    }
    if (context.view === "settings") {
      return context.isGuardian && notificationPreferenceTools.has(name);
    }
    return false;
  });
}

/**
 * Family-wide read tools shared by the parent WebMCP surface and Remote MCP.
 * Validation and URL serialization come from the public Core API contract.
 */
export function createWebMcpSearchHistoryTools(
  workspaceId: string,
  readCore: WebMcpCoreRead,
): WebMcpTool[] {
  return [
    {
      name: "oyako_search_workspace_content_v1",
      title: "家族の内容を検索",
      description:
        "現在ログイン中の家族について、Task、Routine、Project、証跡メモ、振り返りメモを条件検索します。",
      inputSchema: z.toJSONSchema(SearchWorkspaceContentQuerySchema),
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input, { signal }) => {
        const query = SearchWorkspaceContentQuerySchema.parse(input);
        return SearchContentPageResponseSchema.parse(
          await readCore(searchWorkspaceContentPath(workspaceId, query), signal),
        );
      },
    },
    {
      name: "oyako_list_product_history_v1",
      title: "通常の変更履歴を一覧",
      description:
        "現在ログイン中の家族について、Task、Routine、Project、メモの通常履歴をセキュリティ監査と分離して読みます。",
      inputSchema: z.toJSONSchema(ProductHistoryQuerySchema),
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input, { signal }) => {
        const query = ProductHistoryQuerySchema.parse(input);
        return ProductHistoryPageResponseSchema.parse(
          await readCore(productHistoryPath(workspaceId, query), signal),
        );
      },
    },
    {
      name: "oyako_list_security_audit_v1",
      title: "セキュリティ監査を一覧",
      description:
        "現在ログイン中の家族について、秘密情報や詳細本文を含まないセキュリティ監査投影だけを読みます。",
      inputSchema: z.toJSONSchema(SecurityAuditQuerySchema),
      annotations: { readOnlyHint: true, untrustedContentHint: true },
      execute: async (input, { signal }) => {
        const query = SecurityAuditQuerySchema.parse(input);
        return SecurityAuditPageResponseSchema.parse(
          await readCore(securityAuditPath(workspaceId, query), signal),
        );
      },
    },
  ];
}

export type WebMcpRoutineSchedule =
  | { kind: "daily"; interval_days: number }
  | { kind: "weekly"; interval_weeks: number; weekdays: number[] }
  | { kind: "monthly_days"; days: (number | "last")[] }
  | { kind: "monthly_nth_weekday"; ordinal: -1 | 1 | 2 | 3 | 4 | 5; weekday: number };

export function registerWebMcpTools(
  tools: WebMcpTool[],
  target: WebMcpDocument = document as Document & WebMcpDocument,
  reportError: (error: unknown) => void = (error) =>
    console.warn("WebMCP registration failed", error),
): WebMcpRegistration {
  const modelContext = target.modelContext;
  if (modelContext === undefined || typeof modelContext.registerTool !== "function") {
    return { supported: false, dispose: () => undefined };
  }

  const controller = new AbortController();
  for (const tool of tools) {
    try {
      void modelContext
        .registerTool(tool, { signal: controller.signal })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) reportError(error);
        });
    } catch (error) {
      if (!controller.signal.aborted) reportError(error);
    }
  }
  return { supported: true, dispose: () => controller.abort() };
}

export function requireWebMcpString(
  input: Record<string, unknown>,
  field: string,
  maximumLength: number,
): string {
  const value = input[field];
  if (typeof value !== "string") throw new Error(`${field} must be a string.`);
  const normalized = value.trim();
  if (normalized.length === 0 || normalized.length > maximumLength) {
    throw new Error(`${field} must be between 1 and ${String(maximumLength)} characters.`);
  }
  return normalized;
}

export function requireWebMcpInteger(
  input: Record<string, unknown>,
  field: string,
  minimum: number,
  maximum: number,
): number {
  const value = input[field];
  if (!Number.isInteger(value) || typeof value !== "number" || value < minimum || value > maximum) {
    throw new Error(
      `${field} must be an integer between ${String(minimum)} and ${String(maximum)}.`,
    );
  }
  return value;
}

export function requireWebMcpIdempotencyKey(input: Record<string, unknown>): string {
  const value = requireWebMcpString(input, "idempotency_key", 128);
  if (value.length < 16 || !/^[A-Za-z0-9._:-]+$/.test(value)) {
    throw new Error("idempotency_key must be 16-128 safe ASCII characters.");
  }
  return value;
}

export function requireWebMcpDate(input: Record<string, unknown>, field: string): string {
  const value = requireWebMcpString(input, field, 10);
  const parsed = new Date(`${value}T00:00:00Z`);
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
  ) {
    throw new Error(`${field} must be a valid YYYY-MM-DD date.`);
  }
  return value;
}

export function requireWebMcpRoutineSchedule(
  input: Record<string, unknown>,
): WebMcpRoutineSchedule {
  const kind = requireWebMcpString(input, "schedule_kind", 32);
  if (kind === "daily") {
    const interval =
      input.interval === undefined ? 1 : requireWebMcpInteger(input, "interval", 1, 52);
    return { kind, interval_days: interval };
  }
  if (kind === "weekly") {
    const interval =
      input.interval === undefined ? 1 : requireWebMcpInteger(input, "interval", 1, 52);
    const weekdays = input.weekdays;
    if (
      !Array.isArray(weekdays) ||
      weekdays.length === 0 ||
      weekdays.length > 7 ||
      weekdays.some(
        (weekday) =>
          typeof weekday !== "number" || !Number.isInteger(weekday) || weekday < 1 || weekday > 7,
      ) ||
      new Set(weekdays).size !== weekdays.length
    ) {
      throw new Error("weekdays must contain unique ISO weekday integers 1-7.");
    }
    return { kind, interval_weeks: interval, weekdays: weekdays.map(Number) };
  }
  if (kind === "monthly_days") {
    const values = input.month_days;
    if (
      !Array.isArray(values) ||
      values.length === 0 ||
      values.length > 32 ||
      values.some(
        (day) =>
          day !== "last" &&
          (typeof day !== "number" || !Number.isInteger(day) || day < 1 || day > 31),
      ) ||
      new Set(values).size !== values.length
    ) {
      throw new Error("month_days must contain unique integers 1-31 or last.");
    }
    return {
      kind,
      days: values.map((day) => (day === "last" ? "last" : Number(day))),
    };
  }
  if (kind === "monthly_nth_weekday") {
    const ordinal = requireWebMcpInteger(input, "month_ordinal", -1, 5);
    if (![1, 2, 3, 4, 5, -1].includes(ordinal)) {
      throw new Error("month_ordinal must be 1-5 or -1 for the final weekday.");
    }
    const weekday = requireWebMcpInteger(input, "month_weekday", 1, 7);
    return {
      kind,
      ordinal: ordinal as -1 | 1 | 2 | 3 | 4 | 5,
      weekday,
    };
  }
  throw new Error("schedule_kind must be daily, weekly, monthly_days, or monthly_nth_weekday.");
}
